import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useRef, type ReactNode } from "react";

import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/Button";
import { LAYER } from "@/shared/ui/styles";

export type SheetWidth = "md" | "lg" | "xl";

const WIDTH: Record<SheetWidth, string> = {
  md: "max-w-[30rem]",
  lg: "max-w-[36rem]",
  /* Wide enough for a main column and a properties rail beside it (the task panel). */
  xl: "max-w-[36rem] lg:max-w-[62rem]",
};

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Names the panel for screen readers; it is not shown. */
  label: string;
  /** The top bar's content before the close button: a breadcrumb, actions. */
  header?: ReactNode;
  /** Accessible name of the close button. Defaults to "Close". */
  closeLabel?: string;
  /** "md" is 30rem, "lg" 36rem, "xl" 62rem from lg. Phones always get the full screen. */
  width?: SheetWidth;
  /** Focus a specific control on open. By default the panel itself takes focus, so typing
      never edits a field by accident. */
  initialFocus?: () => void;
  children: ReactNode;
}

function blurFocusedField() {
  if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
}

/**
 * A side panel on the end edge (right in English, left in Arabic), over the page.
 *
 * Two conventions keep in-place editing safe:
 * - Escape inside an element marked `data-dirty="true"` does not close the panel, so the field
 *   can use Escape to undo its own edit.
 * - Before the panel closes, the focused element is blurred, so a field that saves on blur
 *   saves before the panel goes away.
 */
export function Sheet({
  open,
  onOpenChange,
  label,
  header,
  closeLabel,
  width = "md",
  initialFocus,
  children,
}: SheetProps) {
  const t = useT();
  const contentRef = useRef<HTMLDivElement>(null);
  const close = closeLabel ?? t("common.close");

  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) blurFocusedField();
        onOpenChange(next);
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className={cn(LAYER.sheetScrim, "fixed inset-0 animate-fade-in bg-scrim-soft")}
        />
        <RadixDialog.Content
          ref={contentRef}
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (initialFocus) initialFocus();
            else contentRef.current?.focus();
          }}
          onEscapeKeyDown={(event) => {
            if (event.target instanceof HTMLElement && event.target.dataset.dirty === "true") {
              event.preventDefault();
            }
          }}
          className={cn(
            LAYER.sheet,
            "fixed inset-y-0 end-0 flex w-full animate-sheet-in flex-col bg-surface shadow-overlay outline-none sm:border-s sm:border-line lg:inset-y-2 lg:end-2 lg:rounded-xl lg:border",
            WIDTH[width],
          )}
        >
          <RadixDialog.Title className="sr-only">{label}</RadixDialog.Title>
          <header className="flex h-12 shrink-0 items-center gap-1 border-b border-line ps-4 pe-2">
            <div className="flex min-w-0 flex-1 items-center gap-1">{header}</div>
            <RadixDialog.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label={close} title={close}>
                <X aria-hidden="true" />
              </Button>
            </RadixDialog.Close>
          </header>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
